import { isValid, parseISO } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { DEFAULT_TIMEZONE } from './common';

/**
 * Anchored bare calendar-day shape (`'YYYY-MM-DD'`) — the same input class
 * `ctCalendarDay()` (ct-time.util.ts) special-cases with a verbatim
 * passthrough. `parseISO` parses a bare date as LOCAL (runtime-TZ) midnight,
 * which made `formatCentralTime`/`formatCentralDate` silently
 * non-deterministic for this one shape despite their "independent of the
 * runtime timezone" contract (PR #2165 round-2 correctness finding, verified
 * by execution: `formatCentralDate('2026-08-15')` rendered Aug 14 under
 * TZ=UTC but Aug 15 under TZ=America/Chicago). Bare dates are instead
 * anchored to CT-midnight via an explicit `zonedTimeToUtc`, which is
 * runtime-TZ independent: a date-only value renders as its own Central
 * calendar day (with a defined 12:00 AM wall clock if a time pattern is
 * requested), byte-identical on every host.
 */
const BARE_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Short human label for the display timezone, e.g. "Central Time (CT)".
 *
 * Use this for a single, section-level timezone note (see
 * {@link TIME_SLOTS_TIMEZONE_NOTE}) instead of repeating a per-slot `zzz`
 * abbreviation on every time button (#1930).
 */
export const DEFAULT_TIMEZONE_LABEL = 'Central Time (CT)';

/**
 * Section-level note telling the customer which timezone every slot/time on the
 * page is shown in. Rendered ONCE near the slot-list heading so the individual
 * slot buttons can stay bare (e.g. "9:00 AM"), not "9:00 AM CST" (#1930).
 */
export const TIME_SLOTS_TIMEZONE_NOTE = `Time slots shown in ${DEFAULT_TIMEZONE_LABEL}`;

/**
 * Format an absolute instant as a bare 12-hour time (default "h:mm a", e.g.
 * "9:00 AM") in the business display timezone (Central), independent of the
 * runtime timezone.
 *
 * Centralized so every booking surface (available-booking-slots, calendar
 * search, event-details, signup-checkout) renders times identically and no
 * surface re-applies the host/runtime offset (#1930). The previous per-surface
 * implementations diverged: some appended a `zzz` zone abbreviation, some used
 * `hh:mm a` (leading zero), and one formatted an already-zoned Date with the
 * bare `format()` (re-applying the runtime offset). This is the single source.
 *
 * Malformed input (an unparseable string, an Invalid Date, or NaN) returns the
 * em-dash placeholder `'—'` instead of letting `formatInTimeZone` throw a
 * RangeError — a single bad row from the API must degrade to a blank cell, not
 * crash the whole list render. Valid inputs are formatted exactly as before.
 *
 * @param value ISO string, epoch ms, or Date representing an absolute instant.
 * @param pattern date-fns format pattern (defaults to bare 12-hour time).
 */
export function formatCentralTime(value: string | number | Date, pattern = 'h:mm a'): string {
  const instant =
    typeof value === 'string'
      ? BARE_DATE_ONLY.test(value.trim())
        ? // Bare 'YYYY-MM-DD': anchor to CT-midnight (see BARE_DATE_ONLY) —
          // parseISO would read it as runtime-TZ-local midnight.
          zonedTimeToUtc(`${value.trim()}T00:00:00`, DEFAULT_TIMEZONE)
        : parseISO(value)
      : value;
  if (!isValid(instant)) {
    return '—';
  }
  return formatInTimeZone(instant, DEFAULT_TIMEZONE, pattern);
}

/**
 * Format an absolute instant as a calendar DATE in the business display
 * timezone (Central) — the date-side sibling of {@link formatCentralTime},
 * same malformed-input degradation (`'—'`), same CT pinning.
 *
 * `TestingEvent.testEventDate` is stored as a CT-anchored instant (#2088), so
 * an evening event serializes past UTC midnight (Aug 14 7:30 PM CDT is
 * `'2026-08-15T00:30:00.000Z'`). Angular's bare `| date:` pipe renders in the
 * VIEWER'S browser timezone: correct-looking in CT, a day late for viewers in
 * UTC/east-of-CT for evening events, and a day EARLY west of CT for
 * UTC-midnight-adjacent instants. Every event-date rendering must pin to CT —
 * the same rule the funnel's times already follow via formatCentralTime.
 * Frontend templates consume this through ui-core's `CentralDatePipe`
 * (`| centralDate`).
 *
 * The default pattern is date-fns' equivalent of Angular DatePipe `'fullDate'`
 * ("Friday, August 14, 2026"). NOTE: patterns here are date-fns tokens — use
 * `'yyyy'`/`'d'`, never Angular/moment `'YYYY'`/`'DD'` (date-fns throws on
 * week-numbering tokens used as calendar years).
 *
 * @param value ISO string, epoch ms, or Date representing an absolute instant.
 * @param pattern date-fns format pattern (defaults to full date).
 */
export function formatCentralDate(value: string | number | Date, pattern = 'EEEE, MMMM d, y'): string {
  return formatCentralTime(value, pattern);
}
